import React from "react";
import { ModuleItem, ReportAssignmentRow, ReportTypeDefinition } from "./types";
import { moduleLabelForTooltip, ruleDescription, rowState } from "./logic";
import { RaIcon } from "./RaIcon";

interface AssignModuleRowProps {
  row: ReportAssignmentRow;
  type: ReportTypeDefinition;
  dueText: string;
  modules: ModuleItem[];
  historyCount: number;
  flash: boolean;
  disabled?: boolean;
  onChooseModules: (anchor: HTMLElement) => void;
  onConfirm: () => void;
  onOpenHistory: () => void;
}

export const AssignModuleRow: React.FC<AssignModuleRowProps> = ({
  row,
  type,
  dueText,
  modules,
  historyCount,
  flash,
  disabled,
  onChooseModules,
  onConfirm,
  onOpenHistory,
}) => {
  const state = rowState(row);
  const edited = row.editedFromRule;
  const rowClass = { needs_modules: "need", to_confirm: "todo", confirmed: "ok" }[state];
  const statusLabel = { needs_modules: "Needs modules", to_confirm: "To confirm", confirmed: "Confirmed" }[
    state
  ];

  let mods: React.ReactNode;
  if (type.who === "none") {
    mods = <span className="nomod">No modules needed. Written from the BOM and production details.</span>;
  } else if (!row.moduleIds.length) {
    mods = (
      <span className="none">
        <RaIcon name="alert" />
        {type.who === "tsd"
          ? `No modules on the ${type.tsdName ?? "report's"} test`
          : "No modules available"}
      </span>
    );
  } else if (type.who === "all" && !edited) {
    mods = (
      <div className="chips">
        <span className="mc all">All {row.moduleIds.length} modules</span>
      </div>
    );
  } else {
    mods = (
      <div className="chips">
        {row.moduleIds.map((id) => {
          const m = modules.find((x) => x.id === id);
          return (
            <span key={id} className="mc" title={moduleLabelForTooltip(m) || undefined}>
              {m?.serial ?? id}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`rr ${rowClass}${flash ? " flash" : ""}`} data-report-id={row.id}>
      <div className="rn">
        <b>{type.name}</b>
        <span>{dueText}</span>
      </div>
      <div>
        {type.who !== "none" && (
          <div className={`rule${edited ? " ed" : ""}`}>
            {edited ? (
              <>
                <RaIcon name="edit" />
                Changed from the rule
              </>
            ) : (
              <>
                <RaIcon name="wand" />
                {ruleDescription(type)}
              </>
            )}
          </div>
        )}
        {mods}
      </div>
      <div>
        <span className={`status ${rowClass}`}>
          <i />
          {statusLabel}
        </span>
      </div>
      <div className="acts">
        <button
          type="button"
          className="ib hbtn"
          title="History for this report"
          aria-label={`History for ${type.name}`}
          onClick={onOpenHistory}
        >
          <RaIcon name="hist" />
          {historyCount > 0 && <i>{historyCount}</i>}
        </button>
        {type.who !== "none" && (
          <button
            type="button"
            className="cbtn"
            disabled={disabled}
            onClick={(e) => onChooseModules(e.currentTarget)}
          >
            <RaIcon name="edit" />
            {row.moduleIds.length ? "Change" : "Choose"}
          </button>
        )}
        {row.confirmed ? (
          <span
            className="cbtn on locked"
            title={row.confirmedBy ? `Confirmed by ${row.confirmedBy}` : undefined}
          >
            <RaIcon name="lock" />
            Confirmed
          </span>
        ) : (
          <button
            type="button"
            className="cbtn"
            disabled={disabled || state === "needs_modules"}
            title={state === "needs_modules" ? "Choose modules first" : undefined}
            onClick={onConfirm}
          >
            <RaIcon name="check" />
            Confirm
          </button>
        )}
      </div>
    </div>
  );
};
